import React, { useState, useEffect } from 'react';

function Question({ question, onAnswer, onNext }) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showNext, setShowNext] = useState(false);
  const [shuffledAnswers, setShuffledAnswers] = useState([]);

  useEffect(() => {
    // Shuffle answers when the question changes
    const answers = Object.entries(question.answers);
    setShuffledAnswers(answers.sort(() => Math.random() - 0.5));
    setSelectedAnswer(null);
    setShowNext(false);
  }, [question]);

  const handleAnswerClick = (answer) => {
    if (selectedAnswer === null) {
      setSelectedAnswer(answer);
      setShowNext(true);
      onAnswer(answer === question.correct_answer);
    }
  };

  const getButtonClass = (answer) => {
    const baseClass = "w-full py-3 px-4 rounded-lg text-left transition-all duration-200 border-2 flex items-center ";

    // After answer is selected
    if (selectedAnswer !== null) {
      // This is the correct answer - always highlight it
      if (answer === question.correct_answer) {
        return baseClass + "border-correct bg-correct-bg text-correct cursor-default";
      }
      // This was selected but is incorrect
      if (answer === selectedAnswer) {
        return baseClass + "border-incorrect bg-incorrect-bg text-incorrect cursor-default";
      }
      // Unselected, not correct
      return baseClass + "border-border bg-transparent text-ink-muted cursor-default opacity-50";
    }

    // Default state - not yet answered
    return baseClass + "border-border bg-transparent text-ink hover:border-accent hover:bg-cream cursor-pointer";
  };

  const getLetterClass = (answer) => {
    const baseClass = "font-display font-semibold mr-3 text-lg ";

    if (selectedAnswer !== null) {
      if (answer === question.correct_answer) {
        return baseClass + "text-correct";
      }
      if (answer === selectedAnswer) {
        return baseClass + "text-incorrect";
      }
      return baseClass + "text-ink-muted";
    }

    return baseClass + "text-accent";
  };

  return (
    <div className="animate-fade-in">
      <h3 className="font-display text-lg sm:text-xl font-medium text-ink mb-6 leading-relaxed">
        {question.question}
      </h3>

      <div className="space-y-3">
        {shuffledAnswers.map(([key, value], index) => (
          <button
            key={key}
            onClick={() => handleAnswerClick(key)}
            className={getButtonClass(key)}
            disabled={selectedAnswer !== null}
            style={{
              animationDelay: `${index * 50}ms`,
              opacity: 0,
              animation: `fade-in 0.4s ease-out ${index * 50}ms forwards`
            }}
          >
            <span className={getLetterClass(key)}>{key}.</span>
            <span className="font-body">{value}</span>
            {selectedAnswer !== null && key === question.correct_answer && (
              <svg
                className="w-5 h-5 ml-auto text-correct"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
            {selectedAnswer === key && key !== question.correct_answer && (
              <svg
                className="w-5 h-5 ml-auto text-incorrect"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        ))}
      </div>

      {showNext && (
        <button
          onClick={onNext}
          className="mt-6 bg-accent hover:bg-accent-dark text-white font-body font-semibold rounded-md px-8 py-3 transition-colors duration-200 animate-fade-in"
        >
          Volgende vraag
        </button>
      )}
    </div>
  );
}

export default Question;
